/**
 * SlotRow — one roster slot as a row of the plan matrix.
 *
 * The row is a rail plus exactly BUCKET_WIDTH cells, on the same grid template
 * as the column headers in DraftPlan, so PRIMARY / BACKUP 1 / BACKUP 2 /
 * BACKUP 3 line up down the whole page. That alignment is the feature — it is
 * what makes the plan readable across rather than down.
 *
 * Bench rows use this component unchanged and at full size. A compacted bench
 * would say the bench matters less, and on a 16-man roster with 6 bench spots
 * that is where a lot of the plan's actual thinking lives.
 *
 * THE RAIL IS THE EARMARK. It holds the slot label, an editable BUDGET, and a
 * delta pill — and the delta compares the primary's price against THAT BUDGET,
 * not against the board. "+5" means the name you listed costs $5 more than you
 * set aside; "-34" means you left $34 on the table at this slot. The board
 * comparison still exists, per target, inside each cell.
 */

import React from "react";
import { theme } from "../../data/theme";
import { BENCH_RESERVE, BUCKET_WIDTH, primaryOf, slotBudget } from "../../hooks/usePlanState";
import TargetCell from "./TargetCell";

// The rail carries an input now, not just a number, so it is wider than the
// 58px it needed when it was read-only. Exported with the template because
// DraftPlan's sticky column headers ride the same grid and must not drift.
export const PLAN_RAIL = 84;
/**
 * 🔴 minmax(0, 1fr), NOT 1fr — and the zero is the whole fix.
 *
 * `1fr` is `minmax(auto, 1fr)`, so every track carries a floor of its content's
 * min-content width. The target card's numbers row does not wrap and cannot
 * shrink, so that floor was 282px a cell: the row demanded 1245px, got 1080px at
 * a 1512px viewport, and the matrix overflowed its column on anything below
 * ~1677px. Invisible at the 1880px cap on a wide monitor, and 29px of headroom
 * on a 16" laptop at 150% scaling.
 *
 * The zero lets the track take the width it is given. What happens INSIDE the
 * card once it is squeezed is TargetCell's problem, and it solves it by
 * wrapping rather than by clipping — see the numbers row there.
 *
 * Measured by `npm run card-check`, which fails if this regresses.
 */
export const PLAN_GRID = `${PLAN_RAIL}px repeat(${BUCKET_WIDTH}, minmax(0, 1fr))`;
/**
 * The same template with one cell track instead of four — what a phone row
 * uses. Same rail, same minmax(0, ...) for the same reason, so a single cell
 * takes the width it is given rather than the width its numbers row wants.
 */
export const PLAN_GRID_ONE = `${PLAN_RAIL}px minmax(0, 1fr)`;
export const PLAN_GAP = 8;

// Column index → plan name. The columns ARE Plan A/B/C/D now.
export const PLAN_LETTERS = ["A", "B", "C", "D"];

/** primary price vs the slot's earmark: +5 over, -34 under, ±0 on the nose. */
function DeltaPill({ delta }) {
  const over = delta > 0;
  const flat = delta === 0;
  const color = flat ? theme.colors.text.faint : (over ? theme.colors.status.danger : theme.colors.status.success);
  return (
    <span
      title={flat
        ? "Your target costs exactly what you earmarked"
        : (over ? `Your target costs $${delta} more than you earmarked` : `You earmarked $${-delta} more than your target costs`)}
      style={{
        display: "inline-block",
        padding: "1px 4px",
        borderRadius: theme.radius.sm,
        background: theme.colors.bg.raised,
        color,
        fontSize: theme.fontSize.small,
        fontWeight: theme.fontWeight.heavy,
        lineHeight: "14px",
      }}
    >
      {flat ? "±0" : (over ? `+${delta}` : `${delta}`)}
    </span>
  );
}

export default function SlotRow({
  slot,
  bucket,
  activeCell,
  posRankFor,
  ppgFor,
  isStarred,
  differs = false,
  fallThroughIdx = -1,
  commitmentCost = 0,
  onReoptimize,
  onSelectCell,
  onPromote,
  onToggleGone,
  onToggleStar,
  onToggleLock,
  ppgOverrideFor,
  enginePpgFor,
  onPpgChange,
  onPpgReset,
  onRemove,
  onPriceChange,
  onBudgetChange,
  baselinePrice = null,
  onlyCellIdx = null,
  selectOnTap = false,
}) {
  const cells = (bucket && bucket.cells) || new Array(BUCKET_WIDTH).fill(null);
  const primary = primaryOf(bucket);

  /**
   * 🔴 A VIEW FILTER, NOT A DIFFERENT ROW. `onlyCellIdx` picks which of the four
   * cells is drawn and changes NOTHING else: the bucket is whole, `primary`
   * still comes from primaryOf over all four, the rail, the delta pill and the
   * fall-through marker are computed exactly as they are on a desktop. So a
   * phone showing Plan C still shows the earmark and the +/- that Plan A's
   * primary sets, which is correct — the budget tracks column A whatever you
   * are looking at, and the summary above says so.
   *
   * Every cell keeps its TRUE index, so every handler below is addressing the
   * bucket position it always addressed. A re-indexed single cell would have
   * quietly written Plan A on every promote from Plan C.
   */
  const shownCells = onlyCellIdx == null
    ? cells.map((target, cellIdx) => [target, cellIdx])
    : [[cells[onlyCellIdx] || null, onlyCellIdx]];

  // budget == null means "follow the primary". The rail still shows a number in
  // that case — the implicit one — but muted, so it reads as a default you can
  // override rather than as a value you set.
  //
  // 🔴 EVERY EMPTY RAIL SHOWS WHAT THAT SPOT IS WORTH, and the two answers come
  // from the same place. A bench spot shows $1, the solver's own per-bench
  // reserve, held back for every open bench slot before the starters are
  // solved. A starter shows what the optimizer paid for that slot solving an
  // empty plan on the whole budget, which is the same question as "what would
  // Fill empty spots do from here" - see useBaselinePlan. Both are constraints
  // or answers the engine is already producing, reported rather than invented.
  //
  // Blank only when the baseline has nothing to say about the slot, which
  // happens when the whole solve failed. A guess would be worse than a blank.
  //
  // 🔴 DISPLAY ONLY. The bucket's budget is still null, so slotBudget,
  // isCommitted, planned, unplanned and maxBid see exactly what they saw
  // before: a muted default is not money set aside.
  const explicit = bucket && bucket.budget != null;
  const earmark = slotBudget(bucket);
  const isOpen = !primary && !explicit;
  const benchDefault = isOpen && slot.pos === "BN";
  const baseline = isOpen && !benchDefault && baselinePrice != null
    ? Math.max(0, Math.round(Number(baselinePrice) || 0))
    : null;
  const shown = explicit
    ? bucket.budget
    : (primary ? earmark : (benchDefault ? BENCH_RESERVE : (baseline != null ? baseline : "")));
  const delta = primary ? Math.round((Number(primary.price) || 0) - earmark) : null;

  return (
    <div data-testid={`slot-row-${slot.id}`} style={{
      display: "grid",
      gridTemplateColumns: onlyCellIdx == null ? PLAN_GRID : PLAN_GRID_ONE,
      gap: PLAN_GAP,
      alignItems: "stretch",
    }}>
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center",
        gap: 3,
        padding: theme.spacing.sm,
        borderRadius: theme.radius.md,
        background: theme.colors.bg.deep,
        border: `1px solid ${theme.colors.border.subtle}`,
      }}>
        <span style={{
          fontSize: theme.fontSize.small,
          fontWeight: theme.fontWeight.heavy,
          color: theme.colors.text.secondary,
          letterSpacing: 0.5,
        }}>{slot.label}</span>

        <span style={{
          fontSize: theme.fontSize.caption,
          fontWeight: theme.fontWeight.heavy,
          letterSpacing: 0.7,
          color: theme.colors.text.faint,
        }}>BUDGET</span>

        {/* 🔴 Same focus rules as the price input in TargetCell: controlled,
            writes straight through on change, select-on-focus, NO local draft
            state, and keyed by slot so it can never remount mid-keystroke.
            🔴 An empty field sets null, NOT 0. Those are different states —
            null hands the slot back to the primary-price default, 0 is a
            deliberate "this slot gets nothing and is still spoken for" — and
            planBudget.test.js pins the difference. */}
        <div style={{ display: "flex", alignItems: "center", gap: 1 }}>
          <span style={{ fontSize: theme.fontSize.small, color: theme.colors.text.faint }}>$</span>
          <input
            key={`budget-${slot.id}`}
            type="number"
            min={0}
            value={shown}
            placeholder="—"
            onFocus={(e) => e.target.select()}
            onChange={(e) => {
              const v = e.target.value;
              onBudgetChange && onBudgetChange(v === "" ? null : v);
            }}
            title={explicit
              ? "Earmarked for this slot"
              : (benchDefault
                ? "Following the bench reserve, type to earmark"
                : (baseline != null
                  ? "Following the board's target for this spot, type to earmark"
                  : "Following your primary's price — type to earmark"))}
            style={{
              width: 46,
              padding: "1px 3px",
              borderRadius: theme.radius.sm,
              border: `1px solid ${explicit ? theme.colors.border.brand : theme.colors.border.default}`,
              background: theme.colors.bg.raised,
              color: explicit ? theme.colors.text.primary : theme.colors.text.faint,
              fontSize: theme.fontSize.small,
              fontWeight: theme.fontWeight.bold,
              fontFamily: theme.fonts.display,
            }}
          />
        </div>

        {primary && <DeltaPill delta={delta} />}

        {/* The two optimizer builds disagreed at this slot. A marker, not a
            diff view — the plan dropdown already lets you flip between them,
            and the useful signal is just "look here". */}
        {/* 🔴 The effective primary came from a later column, so the budget is
            charging you for that plan's pick at this slot while every other
            slot still shows Plan A's. The fall-through is right (see primaryOf)
            — this just says so, and offers the actually-correct fix, which is
            re-solving from what is still available. 7ms. */}
        {/* 🔴 INFORMATION, NOT A SCOLDING. "costs 1.8 PPG" states a fact;
            "suboptimal", a warning colour or an icon would imply the user is
            wrong — and often he is not, because he may know something the
            projection does not. Muted text, no red, and nothing at all when the
            commitment is already optimal. Measured against board prices for
            everyone else; the caveat sits near the summary. */}
        {commitmentCost > 0 && (
          <span
            title="Holding this player, measured on its own against the best roster available at board prices. It assumes everyone else goes for their board price."
            style={{
              fontSize: theme.fontSize.caption,
              color: theme.colors.text.faint,
              whiteSpace: "nowrap",
            }}
          >costs {commitmentCost.toFixed(1)} PPG</span>
        )}

        {fallThroughIdx > 0 && (
          <span
            onClick={onReoptimize}
            title="This slot fell through to a later plan's pick. Re-solving from what is still available is usually the right move."
            style={{
              fontSize: theme.fontSize.caption,
              fontWeight: theme.fontWeight.bold,
              color: theme.colors.text.faint,
              cursor: onReoptimize ? "pointer" : "default",
              textDecoration: onReoptimize ? "underline" : "none",
              textDecorationStyle: "dotted",
            }}
          >Plan {PLAN_LETTERS[fallThroughIdx] || "?"} pick — re-optimize?</span>
        )}

        {differs && (
          <span
            title="The PPG and VORP builds picked different players for this slot"
            style={{
              fontSize: theme.fontSize.caption,
              fontWeight: theme.fontWeight.heavy,
              letterSpacing: 0.4,
              color: theme.colors.status.info,
            }}
          >• differs</span>
        )}
      </div>

      {/* A star is a property of the PLAYER, so `starred` is read off the
          league sheet by key rather than stored on the Target — which is what
          makes one star show on every plan and every column holding him at
          once, with nothing to keep in sync.

          The PRIMARY badge follows primaryOf, NOT cellIdx — see TargetCell.

          🔴 Keyed by cellIdx, deliberately. The row is a fixed positional matrix
          of exactly BUCKET_WIDTH that is never reordered or filtered — promote
          SWAPS contents between two fixed positions — so the index is the true
          identity. Keying on target.key instead would remount a cell on every
          promote and on the first keystroke into a price field. */}
      {shownCells.map(([target, cellIdx]) => (
        <TargetCell
          key={cellIdx}
          target={target}
          cellIdx={cellIdx}
          isActiveCell={!!activeCell && activeCell.slotId === slot.id && activeCell.cellIdx === cellIdx}
          isPrimary={!!target && !!primary && target.key === primary.key}
          starred={!!target && !!isStarred && isStarred(target.key)}
          // 🔴 Read off the TARGET, unlike `starred` directly above it, and the
          // contrast is the whole design. A star is a property of the player and
          // lives in a league sheet, so it shows on every plan holding him at
          // once. A lock is a property of THIS plan's roster — Plan A can own
          // him while Plan B is still shopping — so it lives on the cell.
          locked={!!target && !!target.locked}
          posRank={target && posRankFor ? posRankFor(target) : null}
          ppg={target && ppgFor ? ppgFor(target) : 0}
          {...(onPpgChange ? {
            // 🔴 THE OVERRIDE AND THE ENGINE'S NUMBER ARE SEPARATE PROPS, because
            // the stepper needs both: the override to know whose ink to use and
            // whether to offer the ×, the engine's number as the base that ×
            // goes back to. `ppg` above is the EFFECTIVE one and is what the card
            // has always displayed.
            ppgOverride: target && ppgOverrideFor ? ppgOverrideFor(target) : undefined,
            enginePpg: target && enginePpgFor ? enginePpgFor(target) : 0,
            onPpgChange: (cellIdx2, v) => onPpgChange(cellIdx2, v),
            onPpgReset,
          } : {})}
          onSelect={onSelectCell}
          selectOnTap={selectOnTap}
          onPromote={onPromote}
          onToggleGone={onToggleGone}
          onToggleStar={onToggleStar}
          onToggleLock={onToggleLock}
          onRemove={onRemove}
          onPriceChange={onPriceChange}
        />
      ))}
    </div>
  );
}
